'use client';
import { ArrowRightIcon, CheckCircleIcon, EnvelopeIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Calendar, ChevronRight, CreditCard, Loader2, Tag, User, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { updateAppSession } from '@/core/apis/app-api';
import { getPerks } from '@/core/apis/membership-api';
import { signInMagicLink, validateOTP } from '@/core/queries/auth-queries';
import { CartContext } from '@/core/store/cart/CartProvider';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { setCartData } from '@/core/store/productContext/product-reducers';
import { productSelector } from '@/core/store/productContext/product-states';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { appSettings } from '@/settings/app-settings';
import { useToast } from '@/ux/components/shadcn/ui/use-toast';
import { addToCart, calculateTotal } from '@/ux-legacy/components/common/Cart/CartFunction';

import { ContentItem } from '../../types/ambient-intelligence.types';
import { ProductCard } from '../content-cards';
import { InspectViewRegistry } from '../registries';
import { MembershipVersionSelector } from './MembershipVersionSelector';

interface MembershipInspectViewProps {
  content: ContentItem;
  onClose?: () => void;
  onAction?: (action: string, content: any, data?: any) => void;
}

interface Perk {
  _id: string;
  name: string;
  price: number;
  memberPrice: number;
  discountPercentage: number;
  thumbnailImage?: string;
  currencyCode: string;
  type: number;
}

const MembershipInspectView: React.FC<MembershipInspectViewProps> = ({ content, onClose, onAction }) => {
  const [perks, setPerks] = useState<Perk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [MembershipAssetDetails, setMembershipAssetDetails] = useState<any>(null);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [authState, setAuthState] = useState<
    'initial' | 'email' | 'otp' | 'loading' | 'success' | 'authenticated'
  >('initial');
  const [loginEmail, setLoginEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sessionToken, setSessionToken] = useState('');
  const [authError, setAuthError] = useState('');
  const { toast } = useToast();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const otpRefs = useRef<HTMLInputElement[]>([]);

  // Cart context for opening cart drawer
  const cartState = useContextSelector(CartContext, ([cartState]) => cartState);
  const setCartState = useContextSelector(CartContext, ([, setCartState]) => setCartState);

  // Global state for cart items
  const dispatch = useContextSelector(AppContext, setAppState);
  const productState = useContextSelector(AppContext, productSelector);
  const auth = useContextSelector(AppContext, authSelector);

  // Extract membership data
  const { vendorId, vendorName, vendorLogo, isMember, membership, productName, discountPercentage } =
    content.data || {};

  // Calculate days remaining for members
  const getDaysRemaining = () => {
    if (!membership?.endDate) return null;
    const endDate = new Date(membership.endDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysRemaining = getDaysRemaining();
  const isApproachingExpiry = daysRemaining !== null && daysRemaining < 30;

  // Fetch perks from membership API
  useEffect(() => {
    const fetchPerks = async () => {
      if (!vendorId) {
        setError('Vendor information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call the membership service API
        const response = await getPerks({ vendorId, type: 'ASSET' });
        // fetch(
        //   `https://membership-api.qa.bldr.it/perks?vendorId=${vendorId}&type=ASSET`,
        //   {
        //     headers: {
        //       'Content-Type': 'application/json',
        //       // Add authentication if needed
        //       ...(appSettings.api?.headers || {})
        //     }
        //   }
        // );

        // if (!response.ok) {
        //   throw new Error('Failed to fetch membership perks');
        // }

        // const data = await response.json();
        setMembershipAssetDetails(response?.membershipAssetDetails || null);
        setPerks(response?.perks || []);
      } catch (err) {
        console.error('Error fetching perks:', err);
        setError('Unable to load membership benefits');
      } finally {
        setLoading(false);
      }
    };

    fetchPerks();
  }, [vendorId]);

  // Transform perks to content items for ProductCard
  const transformPerkToContentItem = (perk: any): ContentItem => {
    // Calculate the discounted price if memberPrice is not available but discount is
    let discountedPrice = perk.memberPrice;

    if (!perk.memberPrice && perk.discount && perk.versions?.[0]?.price?.fixedPrice) {
      // Calculate the discounted price using the discount percentage
      discountedPrice =
        perk?.versions?.[0]?.price?.fixedPrice -
        (perk.versions?.[0]?.price?.fixedPrice * perk.discount) / 100;
    }

    const originalPrice = perk.versions?.[0]?.price?.fixedPrice || perk.price;
    const savings =
      originalPrice && discountedPrice
        ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
        : perk.discount || 0;

    return {
      id: perk._id,
      type: 'product',
      title: perk.assetDetails?.name,
      description: perk?.assetDetails?.shortDescription || `Members save ${savings}%`,
      media: perk?.assetDetails?.thumbnailImage
        ? {
            thumbnail: {
              url: appendImageRepoDomainIfNotExistInUrl(perk.assetDetails?.thumbnailImage),
              alt: perk?.assetDetails?.name,
            },
          }
        : undefined,
      data: {
        price: discountedPrice,
        originalPrice: originalPrice,
        currency: perk.currencyCode,
        productType: perk.type,
        assetId: perk.assetId,
        discountPercentage: perk.discount || savings,
        membershipBenefit: true,
      },
    };
  };

  const displayedPerks = showAll ? perks : perks.slice(0, 12);

  // Handle membership purchase
  const handleBecomeMember = () => {
    if (!MembershipAssetDetails) {
      toast({
        title: 'Error',
        description: 'Membership details not available',
        variant: 'destructive',
      });
      return;
    }

    // Check if there are multiple versions
    if (MembershipAssetDetails.versions && MembershipAssetDetails.versions.length > 1) {
      setShowVersionSelector(true);
    } else if (MembershipAssetDetails.versions && MembershipAssetDetails.versions.length === 1) {
      // Direct add to cart for single version
      handleAddToCart(MembershipAssetDetails.versions[0]);
    } else {
      toast({
        title: 'Error',
        description: 'No membership plans available',
        variant: 'destructive',
      });
    }
  };

  // Handle start authentication
  const handleStartAuth = () => {
    setAuthState('email');
    setAuthError('');
    setLoginEmail('');
    setTimeout(() => emailInputRef.current?.focus(), 100);
  };

  // Handle email submission for magic link
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginEmail.includes('@')) {
      setAuthError('Please enter a valid email');
      return;
    }

    setAuthState('loading');
    setAuthError('');

    try {
      const result = await signInMagicLink(appSettings.api?.GRAPHQL, loginEmail);

      if (result?.data?.signInMagicLink?.session) {
        setSessionToken(result.data.signInMagicLink.session);
        setAuthState('otp');
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setAuthError('Failed to send verification code');
        setAuthState('email');
      }
    } catch (error) {
      setAuthError('Something went wrong. Please try again.');
      setAuthState('email');
    }
  };

  // Handle OTP change
  const handleOTPChange = (index: number, value: string) => {
    if (value.match(/^[0-9]$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-advance to next input
      if (index < 5) {
        otpRefs.current[index + 1]?.focus();
      }

      // Auto-submit if complete
      if (index === 5 && newOtp.every((digit) => digit !== '')) {
        handleOTPSubmit(newOtp.join(''));
      }
    }
  };

  // Handle OTP paste
  const handleOTPPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedValue = event.clipboardData.getData('text').trim();

    // Extract only numeric characters from pasted data
    const numericData = pastedValue.replace(/\D/g, '');

    if (numericData.length > 0) {
      const newOtp = Array(6).fill('');

      // Fill the OTP array with pasted digits (max 6)
      numericData
        .slice(0, 6)
        .split('')
        .forEach((value, index) => {
          if (index < 6) {
            newOtp[index] = value;
          }
        });

      setOtp(newOtp);

      // Focus the appropriate input
      const filledCount = Math.min(numericData.length, 6);
      if (filledCount >= 6) {
        // If all 6 digits were pasted, focus the last input
        otpRefs.current[5]?.focus();
      } else {
        // Otherwise, focus the next empty input
        otpRefs.current[filledCount]?.focus();
      }

      // Auto-submit if complete after 1 second delay
      if (newOtp.every((digit) => digit !== '')) {
        setTimeout(() => {
          handleOTPSubmit(newOtp.join(''));
        }, 1000);
      }
    }
  };

  // Handle OTP key down
  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP submission
  const handleOTPSubmit = async (otpCode?: string) => {
    const code = otpCode || otp.join('');
    if (code.length !== 6) {
      setAuthError('Please enter all 6 digits');
      return;
    }

    setAuthState('loading');
    setAuthError('');

    try {
      const result = await validateOTP(appSettings.api?.GRAPHQL, code, loginEmail, sessionToken);

      if (result?.data?.respondToChallenge) {
        const user = result.data.respondToChallenge;

        // Update session
        await updateAppSession({
          jwt: result?.data?.respondToChallenge?.jwt,
          refreshToken: result?.data?.respondToChallenge?.refreshToken,
        });

        // Check if user is a member
        // You'll need to implement this check based on your member data

        setAuthState('success');

        // Show success state for 2 seconds
        // setTimeout(() => {
        window.location.reload();
        // if (isMemberNow) {
        //   // Refresh to show member benefits
        //   window.location.reload();
        // } else {
        //   // Show authenticated but not member
        //   setAuthState('authenticated');
        // }
        // }, 2000);
      } else {
        setAuthError('Invalid code. Please try again.');
        setAuthState('otp');
      }
    } catch (error) {
      setAuthError('Invalid code. Please try again.');
      setAuthState('otp');
    }
  };

  // Handle adding membership to cart
  const handleAddToCart = async (selectedVersion: any) => {
    try {
      setIsAddingToCart(true);
      setShowVersionSelector(false);

      // Debug: Log membership details

      const lineItemPayload = {
        assetId: MembershipAssetDetails._id,
        versionId: selectedVersion._id,
        quantity: 1,
        price: selectedVersion.price?.fixedPrice || 0,
        productName: MembershipAssetDetails.name || selectedVersion.name || 'Membership',
        productImage: MembershipAssetDetails.thumbnailImage || MembershipAssetDetails.image,
        productType: 6, // LISTITEM_TYPE_BOLDER_ASSET_MEMBERSHIP
        vendorId: MembershipAssetDetails.vendorId || vendorId,
      };

      const result = await addToCart(lineItemPayload, selectedVersion);

      if (result) {
        // Debug: Log the result to check its structure

        // Calculate new totals with the added item
        const { total, discountAmount, subTotal, taxAmount } = calculateTotal([
          ...productState.dataObj,
          result,
        ]);

        // Update the global cart state with the new membership item
        dispatch(
          setCartData({
            dataObj: [...(productState?.dataObj || []), result || []],
            currencyCode: result?.currencyCode || productState.currencyCode,
            amount: total,
            subTotal,
            taxAmount,
            discountAmount: discountAmount,
          }),
        );

        // Debug: Log the updated cart state
        // console.log('Updated cart state:', {
        //   dataObj: [...productState?.dataObj, result],
        //   total,
        //   subTotal,
        //   taxAmount,
        //   discountAmount
        // });

        toast({
          title: 'Success!',
          description: 'Membership added to cart successfully!',
        });

        // Open the cart drawer with blur effect
        setCartState({ ...cartState, open: true });

        // Close the membership inspect view
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 300); // Small delay to allow cart animation to start
        }

        // Trigger cart update action if available
        onAction?.('cart-updated', { item: result });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to add membership to cart',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while adding to cart',
        variant: 'destructive',
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <div className="inspect-view-container flex flex-col h-full bg-white">
      {/* Header */}
      <div className="inspect-theme-header-bg border-b inspect-theme-border">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-xl font-semibold inspect-theme-text">Membership Benefits</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative">
        <div className="p-6 inspect-theme-bg-elevated">
          <div className="flex items-start gap-4">
            {vendorLogo && (
              <img src={vendorLogo} alt={vendorName} className="w-16 h-16 rounded-xl object-cover" />
            )}
            <div className="flex-1">
              <h3 className="text-2xl font-bold inspect-theme-text mb-2">{vendorName} Membership</h3>

              {isMember ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 inspect-theme-text-muted" />
                    <span className="font-mono text-sm inspect-theme-text">
                      {membership?.shortId || 'Member'}
                    </span>
                  </div>
                  {membership?.endDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 inspect-theme-text-muted" />
                      <span className="text-sm inspect-theme-text">
                        {isApproachingExpiry ? (
                          <span className="inspect-theme-text-warning font-medium">
                            Expires in {daysRemaining} days
                          </span>
                        ) : (
                          `Valid until ${new Date(membership.endDate).toLocaleDateString()}`
                        )}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-lg inspect-theme-text-muted mb-3">
                    Unlock exclusive member pricing and benefits
                  </p>
                  {productName && discountPercentage && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full inspect-theme-bg-primary/10">
                      <Tag className="w-4 h-4 inspect-theme-text-primary" />
                      <span className="text-sm font-medium inspect-theme-text-primary">
                        Save {discountPercentage}% on {productName}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CTA for non-members */}
          {!isMember && !showLoginForm && (
            <>
              <button
                id="become-member-cta"
                className="w-full mt-6 px-6 py-3 rounded-lg inspect-theme-button-primary font-medium transition-all hover:opacity-90 flex items-center justify-center gap-2"
                onClick={handleBecomeMember}
                disabled={isAddingToCart}
              >
                {isAddingToCart ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </>
                ) : (
                  <>
                    Become a Member
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
              {!auth?.loggedIn && (
                <button
                  className="w-full mt-3 px-6 py-3 rounded-lg border inspect-theme-border inspect-theme-text font-medium transition-all hover:bg-gray-50 flex items-center justify-center gap-2"
                  onClick={() => {
                    setShowLoginForm(true);
                    handleStartAuth();
                  }}
                >
                  <User className="w-4 h-4" />
                  Existing Member
                </button>
              )}
            </>
          )}

          {/* Login form for existing members */}
          {!isMember && showLoginForm && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold inspect-theme-text">Member Login</h4>
                <button
                  onClick={() => {
                    setShowLoginForm(false);
                    setAuthState('initial');
                    setLoginEmail('');
                    setOtp(['', '', '', '', '', '']);
                    setAuthError('');
                  }}
                  className="text-sm inspect-theme-text-muted hover:inspect-theme-text"
                >
                  Cancel
                </button>
              </div>

              {/* Render auth flow based on state */}
              {authState === 'email' && (
                <form onSubmit={handleEmailSubmit} className="space-y-4 animate-in fade-in duration-300">
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={emailInputRef}
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="Enter your member email"
                      className="w-full pl-12 pr-14 py-4 text-base border-2 border-gray-200 rounded-full bg-white transition-all focus:outline-none focus:border-blue-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]"
                      required
                    />
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                      disabled={!loginEmail}
                    >
                      <ArrowRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                  {authError && <p className="text-red-500 text-sm mt-2">{authError}</p>}
                </form>
              )}

              {authState === 'otp' && (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold inspect-theme-text mb-2">Check your email</h3>
                    <p className="text-sm inspect-theme-text-muted mb-6">
                      We sent a verification code to <strong>{loginEmail}</strong>
                    </p>

                    <div className="flex gap-2 justify-center mb-4">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => {
                            otpRefs.current[index] = el!;
                          }}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOTPChange(index, e.target.value)}
                          onKeyDown={(e) => handleOTPKeyDown(index, e)}
                          onPaste={handleOTPPaste}
                          className="w-12 h-12 text-xl font-semibold text-center border-2 rounded-xl transition-all bg-white border-gray-200 focus:outline-none focus:border-blue-500 focus:transform focus:scale-105 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]"
                          aria-label={`Digit ${index + 1}`}
                        />
                      ))}
                    </div>

                    {authError && <p className="text-red-500 text-sm mb-4">{authError}</p>}

                    <button
                      onClick={() => setAuthState('email')}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Use a different email
                    </button>
                  </div>
                </div>
              )}

              {authState === 'loading' && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  <p className="text-gray-600 mt-4">
                    {sessionToken ? 'Verifying code...' : 'Sending verification...'}
                  </p>
                </div>
              )}

              {authState === 'success' && (
                <div className="text-center py-8 animate-in zoom-in duration-300">
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-semibold inspect-theme-text">Success!</p>
                  <p className="text-sm text-gray-600 mt-2">Checking your membership status...</p>
                </div>
              )}

              {authState === 'authenticated' && (
                <div className="text-center py-8">
                  <SparklesIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="inspect-theme-text">
                    You&apos;re logged in! You can now become a member to access exclusive benefits.
                  </p>
                  <button
                    onClick={() => {
                      setShowLoginForm(false);
                      setAuthState('initial');
                    }}
                    className="mt-4 px-6 py-2 rounded-lg border inspect-theme-border hover:bg-gray-50 transition-colors"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress bar for approaching expiry */}
        {isMember && isApproachingExpiry && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
              style={{ width: `${(daysRemaining / 30) * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Benefits Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <h4 className="text-lg font-semibold inspect-theme-text mb-4">
          {isMember ? 'Your Exclusive Discounts' : 'Member Pricing Available'}
        </h4>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin inspect-theme-text-muted" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border inspect-theme-border hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        ) : perks.length === 0 ? (
          <div className="text-center py-12">
            <p className="inspect-theme-text-muted">No membership benefits available at this time</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedPerks.map((perk) => (
                <ProductCard
                  key={perk._id}
                  content={transformPerkToContentItem(perk)}
                  variant="card"
                  onAction={onAction}
                  className="h-full"
                />
              ))}
            </div>

            {perks.length > 12 && !showAll && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowAll(true)}
                  className="px-6 py-2 rounded-lg border inspect-theme-border hover:bg-gray-50 transition-colors"
                >
                  Show More ({perks.length - 12} more)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Version Selector Modal */}
      {MembershipAssetDetails && showVersionSelector && (
        <MembershipVersionSelector
          isOpen={showVersionSelector}
          onClose={() => setShowVersionSelector(false)}
          versions={MembershipAssetDetails.versions || []}
          membershipName={MembershipAssetDetails.name || vendorName + ' Membership'}
          onVersionSelect={handleAddToCart}
          currencyCode={MembershipAssetDetails.vendor?.currencyCode || 'NOK'}
          isLoading={isAddingToCart}
        />
      )}
    </div>
  );
};

// Register the inspect view
InspectViewRegistry.register('membership', MembershipInspectView);

export default MembershipInspectView;
